<script setup>
import { ChevronDownIcon } from "lucide-vue-next";
import { reactive, ref } from "vue";

import InfiniteLoading from "@/Components/InfiniteLoading.vue";
import SimpleMenu from "@/Components/SimpleMenu.vue";
import SimpleMenuItem from "@/Components/SimpleMenuItem.vue";

import { useDateFormatter } from "@/composables/dateFormatter";
import { useTeam } from "@/composables/team";
import CommentForm from "@campaign-js/Components/CommentForm.vue";
import { Link, usePage } from "@inertiajs/vue3";

const props = defineProps({
  depth: {
    type: Number,
    required: false,
    default: 0
  },
  campaign: {
    type: Object,
    required: true
  },
  page: {
    type: Object,
    required: true
  },
  comment: {
    type: Object,
    required: true
  },
  isLoggedIn: {
    type: Boolean,
    required: true
  },
  onCreate: {
    type: Function,
    required: true
  },
  onUpdate: {
    type: Function,
    required: true
  },
  onDelete: {
    type: Function,
    required: true
  }
});

const commentData = reactive({ ...props.comment });

const _page = usePage();
const { team } = useTeam();
const { dateFromNow } = useDateFormatter();

const isProcessing = ref(false);
const pageNum = ref(1);
const paginate = reactive({ data: [], total: 0 });
const parentCommentId = ref(false);
const replyCommentId = ref(null);
const editCommentId = ref(null);
const infiniteId = ref(new Date().getTime());

const isOwnComment = (comment) => {
  return _page.props.user && comment.commentator.hashid === _page.props.user.hashid;
};

const toDateString = (date) => {
  return dateFromNow(date);
};

const openUpdateBox = (comment) => {
  editCommentId.value = comment.hashid;
};

const updateComment = (comment) => {
  props.onUpdate(comment);
  editCommentId.value = null;
};

const textLines = (text) => {
  return text
    ?.replace(/\r\n|\r|\n/g, "\n")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
};

const openNestedComments = (comment) => {
  parentCommentId.value === comment.hashid ? (parentCommentId.value = null) : (parentCommentId.value = comment.hashid);
};

const openReplyComment = (comment) => {
  replyCommentId.value === comment.hashid ? (replyCommentId.value = null) : (replyCommentId.value = comment.hashid);
};

const fetchNestedComments = ($state) => {
  isProcessing.value = true;

  axios(route("app.ajax.pages.comments.nested", [team.value, props.page, props.comment]), {
    params: { page: pageNum.value }
  })
    .then(({ data }) => {
      if (data.data.length === 0) {
        $state?.complete();
      } else {
        paginate.data = [...paginate.data, ...data.data];
        paginate.total = data.meta.total;
        pageNum.value++;
        $state.loaded();
      }
    })
    .catch(() => {
      $state?.error();
    })
    .finally(() => {
      isProcessing.value = false;
    });
};

const reloadReply = async () => {
  commentData.nested_comments_count++;
  reload();
};

const reset = () => {
  infiniteId.value = new Date().getTime();
  pageNum.value = 1;
  paginate.data = [];
  paginate.total = 0;
  parentCommentId.value = null;
  replyCommentId.value = null;
  editCommentId.value = null;
};

const reload = () => {
  reset();
  fetchNestedComments();
};
</script>

<template>
  <article class="group overflow-x-hidden" :class="depth === 0 ? 'px-6 py-4' : 'py-2'">
    <div class="flex items-start">
      <div class="flex-none pr-3">
        <Link :href="route('user-home', comment.commentator)" class="cursor-pointer">
          <img
            class="rounded-full"
            :src="commentData.commentator.profile_photo_url"
            :alt="commentData.commentator.nickname"
            :class="depth === 0 ? 'h-9 w-9' : 'h-6 w-6'"
          />
        </Link>
      </div>
      <div class="flex-1">
        <header class="flex items-center justify-between">
          <div class="flex items-center">
            <p class="mr-3 inline-flex items-center text-zinc-900" :class="depth === 0 ? 'text-xs+' : 'text-xs'">
              <span>{{ commentData.commentator.nickname }}</span>
            </p>

            <p class="text-xs text-zinc-600">
              <time>{{ toDateString(commentData.created_at) }}</time>
            </p>
          </div>

          <SimpleMenu v-if="isOwnComment(comment)" class="transition group-hover:opacity-100 lg:opacity-0" menu-items-class="right-4">
            <SimpleMenuItem class="text-sm+" @click="openUpdateBox(comment)">
              <span>수정</span>
            </SimpleMenuItem>
            <SimpleMenuItem class="text-sm+" @click="onDelete(comment)">
              <span>삭제</span>
            </SimpleMenuItem>
          </SimpleMenu>
        </header>

        <div class="relative mt-0.5 rounded-lg text-zinc-900" :class="depth === 0 ? 'text-sm+' : 'text-sm'">
          <div v-if="editCommentId === comment.hashid" class="mt-2 space-y-2">
            <textarea v-model="commentData.text" class="block h-full w-full rounded-lg border border-zinc-200"></textarea>
            <div class="flex justify-end">
              <div class="flex items-center space-x-2">
                <button class="rounded-lg border border-zinc-400 px-4 py-1 font-semibold" @click="editCommentId = null">닫기</button>
                <button class="rounded-lg bg-primary px-4 py-1 font-semibold text-[#fff]" @click="updateComment(commentData)">수정</button>
              </div>
            </div>
          </div>

          <p v-else class="break-all">
            <template v-for="(text, index) in textLines(comment.text)" :key="index">
              <span>{{ text }}</span> <br />
            </template>
          </p>
        </div>

        <footer class="mt-2">
          <div class="flex items-center space-x-4">
            <button v-if="commentData.nested_comments_count > 0" class="text-sm text-primary" @click="openNestedComments(comment)">
              <ChevronDownIcon
                class="inline-block h-4 w-4 transform"
                :class="{
                  'rotate-180': parentCommentId === comment.hashid
                }"
              />
              <span> 답글 {{ commentData.nested_comments_count }}개 </span>
            </button>

            <button v-if="isLoggedIn && depth === 0" class="text-xs+ font-semibold text-zinc-600" @click="openReplyComment(comment)">답글</button>
          </div>

          <div v-if="replyCommentId === comment.hashid" class="mt-4">
            <CommentForm
              :campaign="campaign"
              :page="page"
              :parent-comment="comment"
              :is-logged-in="_page.props.user !== null"
              :is-processing="isProcessing"
              submit-text="답글"
              placeholder="답글을 입력해주세요"
              :on-create="onCreate"
              @created="reloadReply"
            />
          </div>

          <div v-if="parentCommentId === comment.hashid" class="mt-4">
            <CommentItem
              v-for="item in paginate.data"
              :key="item.hashid"
              :depth="1"
              :campaign="campaign"
              :page="page"
              :comment="item"
              :is-logged-in="isLoggedIn"
              :on-create="onCreate"
              :on-update="onUpdate"
              :on-delete="onDelete"
            />

            <InfiniteLoading :identifier="infiniteId" @infinite="fetchNestedComments" />
          </div>
        </footer>
      </div>
    </div>
  </article>
</template>
